/** Tenant data is resolved after sign-in from the platform database (M21a). */
import type { UserRole } from '../types';
export const GUEST_REPLY_SETTINGS_ROLES: UserRole[] = ['manager', 'admin', 'owner'];
export const LEGACY_TENANT_ID = 'xzllenz';
/** PO 2026-10-28: fixed display form of the legacy tenant; its platform document is not readable from other tenants. */
export const LEGACY_TENANT_DISPLAY_NAME = 'XZLLENZ';
export type CellFeature = string;
export type CellFeatureState = { readonly status: 'loading' } | { readonly status: 'ready'; readonly features: ReadonlySet<CellFeature> } | { readonly status: 'error' };
export interface CellRuntime { readonly features: ReadonlySet<CellFeature> }
export type HouseColor = `house-${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`;
export interface TenantProperty { readonly key: string; readonly label: string; readonly color: HouseColor; readonly propertyId: string; readonly sortOrder: number }
export interface TenantRuntime {
  readonly tenantId: string;
  readonly displayName: string;
  readonly databaseId: string;
  readonly legacy: boolean;
  readonly access: 'legacy-role' | 'membership';
  readonly role: UserRole;
  readonly apiBase: string;
  readonly serviceUrls?: { businessServices?: string; voiceTools?: string; microsoftConnect?: string };
  readonly sharedMailbox?: string;
  readonly properties: readonly TenantProperty[];
  readonly propertiesUnavailable?: boolean;
}
export type TenantWebConfig = TenantRuntime;
let runtime: TenantRuntime | null = null;
let runtimeGeneration = 0;
export function getTenantGeneration(): number { return runtimeGeneration; }
export function setTenantRuntime(value: TenantRuntime | null): void { runtime = value; runtimeGeneration++; }
export function peekTenantRuntime(): TenantRuntime | null { return runtime; }
export function getTenantRuntime(): TenantRuntime { if (!runtime) throw new Error('Mandant noch nicht aufgelöst'); return runtime; }
export function hasDashboardRole(config: TenantRuntime, need: UserRole | UserRole[]): boolean {
  const canonical = (role: UserRole) => role === 'admin' ? 'owner' : role === 'housekeeping' || role === 'support' ? 'staff' : role;
  return (Array.isArray(need) ? need : [need]).some(role => config.legacy ? role === config.role : canonical(role) === canonical(config.role));
}
export const FEATURE_ASSIGNMENTS: { routes: Record<string, CellFeature | readonly CellFeature[] | null>; widgets: Record<DashboardWidget, CellFeature> } = {
  routes: {
    '/': 'dashboard', '/dashboard': 'dashboard', '/login': 'dashboard', '/bookings': 'dashboard',
    '/cleaning': 'dashboard', '/tasks': 'dashboard', '/calendar': 'dashboard', '/rules': 'dashboard', '/settings/pricing': 'dashboard',
    '/messages': ['guest_chat', 'email_analysis', 'voicebot'], '/system/email-analysis': 'email_analysis',
    '/emails': 'email_analysis', '/settings/mailbox': 'email_analysis', '/support': 'guest_chat', '/settings/replies': 'guest_chat',
    '/voicebot': 'voicebot', '/faq': 'voicebot', '/settings/team': ['dashboard', 'cleaning_preview'], '/alerts': ['dashboard', 'cleaning_preview'],
    '/monitoring': 'dashboard', '/data-explorer': 'dashboard', '/users': 'dashboard', '/feedback': 'guest_reviews',
  },
  widgets: { voicebot: 'voicebot', agentChat: 'agent_tools', pmRequests: 'email_analysis', bookingCases: 'guest_chat', mailbox: 'email_analysis', cleaningPreview: 'cleaning_preview', cleaningTeams: 'cleaning_preview', taskTeam: 'dashboard' },
};
export type DashboardWidget = 'voicebot' | 'agentChat' | 'pmRequests' | 'bookingCases' | 'mailbox' | 'cleaningPreview' | 'cleaningTeams' | 'taskTeam';

/** Without a feature state nothing feature-bound is enabled in a cell (routes mapped to null never are). */
const NOT_LOADED: CellFeatureState = { status: 'loading' };

/**
 * sepipe: REQ-HotelTenancy-1158 REQ-HotelTenancy-108 Legacy build: every screen. Cell build: only screens
 * whose feature the cell reported; while loading or after a load error nothing feature-bound is on.
 */
export function isRouteEnabled(path: string, features: CellFeatureState = NOT_LOADED, config: TenantWebConfig | null = peekTenantRuntime()): boolean {
  if (!config) return false;
  const [pathname, query] = path.split('?');
  const feature = pathname === '/messages' && new URLSearchParams(query).get('view') === 'emails'
    ? 'email_analysis' : FEATURE_ASSIGNMENTS.routes[pathname];
  return config.legacy || (!!feature && features.status === 'ready' && (Array.isArray(feature) ? feature : [feature]).some(value => features.features.has(value)));
}

/** sepipe: REQ-HotelTenancy-1158 REQ-HotelTenancy-108 Same rule as isRouteEnabled for dashboard widgets. */
export function isWidgetEnabled(widget: DashboardWidget, features: CellFeatureState, config: TenantWebConfig | null = peekTenantRuntime()): boolean {
  return !!config && (config.legacy || (features.status === 'ready' && features.features.has(FEATURE_ASSIGNMENTS.widgets[widget])));
}

/** sepipe: REQ-HotelTenancy-1158 Validates the GET /api/cell/features payload against this build's tenant. */
export function parseCellFeatures(payload: unknown, config: TenantWebConfig | null = peekTenantRuntime()): ReadonlySet<CellFeature> {
  if (!config) throw new Error('Mandant noch nicht aufgelöst');
  const data = (payload as { success?: unknown; data?: { tenantId?: unknown; legacy?: unknown; features?: unknown } } | null)?.data;
  if ((payload as { success?: unknown } | null)?.success !== true || !data || !Array.isArray(data.features)
    || !data.features.every(feature => typeof feature === 'string')) {
    throw new Error('Invalid cell feature response');
  }
  if (data.tenantId !== config.tenantId || data.legacy !== config.legacy) {
    throw new Error(`Cell feature response belongs to tenant ${String(data.tenantId)}, not ${config.tenantId}`);
  }
  return new Set(data.features as string[]);
}


/** #613: one wording for a house or unit that iLoca/the sync could not assign. */
export const UNASSIGNED_HOUSE_LABEL = 'Standort ohne Zuordnung';
const UNASSIGNED_HOUSE_LABEL_EN = 'Location not assigned';
const UNASSIGNED_UNIT_LABEL_EN = 'Unit not assigned';
/** The one text for a unit that is not assigned (bookingRoomLabel, rows, calendar). */
export const UNASSIGNED_UNIT_LABEL = 'Einheit ohne Zuordnung';
/** House/location name from the tenant's iLoca-derived property list; placeholders become "Standort ohne Zuordnung". */
export function houseLabel(code: string | null | undefined, config: TenantWebConfig | null = peekTenantRuntime(), language: 'de' | 'en' = 'de'): string {
  const key = code?.trim();
  if (!key || /^(?:unknown|unbekannt|\?)$/i.test(key)) return language === 'en' ? UNASSIGNED_HOUSE_LABEL_EN : UNASSIGNED_HOUSE_LABEL;
  return config?.properties.find(property => property.key.toLowerCase() === key.toLowerCase())?.label ?? key;
}
export function siteLabel(code: string, _features: CellFeatureState, config: TenantWebConfig | null = peekTenantRuntime()): string {
  return houseLabel(code, config);
}
/** Unit (room/apartment) name as iLoca delivers it; missing, "unknown" or legacy placeholders become "Einheit ohne Zuordnung". */
export function unitLabel(room: string | null | undefined, language: 'de' | 'en' = 'de'): string {
  const value = room?.trim();
  return !value || /^(?:unknown|unbekannt|\?|TBD|TBA|Zimmer (?:noch )?nicht zugeordnet|Einheit ohne Zuordnung)$/i.test(value) ? (language === 'en' ? UNASSIGNED_UNIT_LABEL_EN : UNASSIGNED_UNIT_LABEL) : value;
}
/** Backend notificationScope legacy site keys; ER combines both Erlangen sites. */
const LEGACY_NOTIFICATION_SITES: Readonly<Record<string, readonly string[]>> = {
  B52: ['b52'], DP11: ['dp11'], OAu: ['oau'], ER: ['erlangen-haus', 'erlangen-studios'],
};
export function notificationSitesForProperty(property: TenantProperty, config: TenantRuntime): readonly string[] {
  return config.legacy ? LEGACY_NOTIFICATION_SITES[property.key] ?? [] : [property.propertyId];
}
/** Fixed property codes, never guest free text. Preserve the legacy room-code variants. */
export function propertyKeyOf(item: { propertyShortName?: string | null; roomNumber?: string | null }, config: TenantWebConfig | null = peekTenantRuntime()): string | null {
  if (!config) return null;
  const code = item.propertyShortName?.trim().toLowerCase();
  const direct = config.properties.find(property => property.key.toLowerCase() === code);
  if (direct) return direct.key;
  if (!config.legacy) return null;
  for (const property of config.properties) {
    const escaped = property.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`\\b${escaped}${property.key.toLowerCase() === 'oau' ? '' : '\\b'}`, property.key === 'ER' ? '' : 'i');
    if (pattern.test(item.roomNumber ?? '')) return property.key;
  }
  return null;
}
export const NO_TENANT_ACCESS_MESSAGE = 'Kein Zugang';
